import React, { useState, useEffect } from 'react';

const AnnouncementAdmin = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentAnnouncement, setCurrentAnnouncement] = useState({
    title: '',
    content: '',
    active: false,
    start_date: '',
    end_date: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch('/announcements/list', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        credentials: 'same-origin'
      });

      const data = await response.json();
      setAnnouncements(data.announcements);
      setTotalUsers(data.total_users);
      
      // Load the current active announcement for editing
      const active = data.announcements.find(a => a.status === 'active');
      if (active) {
        setCurrentAnnouncement({
          title: active.title,
          content: active.content,
          active: active.active,
          start_date: formatDateForInput(active.start_date),
          end_date: formatDateForInput(active.end_date)
        });
        setEditingId(active.id);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      showMessage('Error loading announcements', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentAnnouncement(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingId ? `/announcements/${editingId}` : '/announcements';
      const method = editingId ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          announcement: currentAnnouncement,
          create_new_version: true
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        showMessage(data.message || 'Announcement saved successfully', 'success');
        fetchAnnouncements();
      } else {
        showMessage(data.errors?.join(', ') || 'Error saving announcement', 'error');
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      showMessage('Error saving announcement', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      const response = await fetch(`/announcements/${id}/activate`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        credentials: 'same-origin'
      });

      const data = await response.json();
      showMessage(data.message, 'success');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error activating announcement:', error);
      showMessage('Error activating announcement', 'error');
    }
  };

  const handleDeactivate = async (id) => {
    if (!confirm('Are you sure you want to stop showing this announcement?')) return;

    try {
      const response = await fetch(`/announcements/${id}/deactivate`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        credentials: 'same-origin'
      });

      const data = await response.json();
      showMessage(data.message, 'success');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deactivating announcement:', error);
      showMessage('Error deactivating announcement', 'error');
    }
  };

  const handleResetAcknowledgments = async (id) => {
    if (!confirm('This will show the announcement to all users again. Continue?')) return;

    try {
      const response = await fetch(`/announcements/${id}/reset_acknowledgments`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        credentials: 'same-origin'
      });

      const data = await response.json();
      showMessage(data.message, 'success');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error resetting acknowledgments:', error);
      showMessage('Error resetting acknowledgments', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this announcement? This cannot be undone.')) return;

    try {
      const response = await fetch(`/announcements/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        credentials: 'same-origin'
      });

      const data = await response.json();
      showMessage(data.message, 'success');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      showMessage('Error deleting announcement', 'error');
    }
  };

  const loadAnnouncement = (announcement) => {
    setCurrentAnnouncement({
      title: announcement.title,
      content: announcement.content,
      active: false, // Always start as inactive for reuse
      start_date: '',
      end_date: ''
    });
    setEditingId(null); // This will create a new version
    window.scrollTo(0, 0);
    showMessage('Announcement loaded for reuse. Make your changes and save.', 'info');
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const getStatusBadge = (announcement) => {
    const statusClass = {
      active: 'status-badge-active',
      inactive: 'status-badge-inactive',
      scheduled: 'status-badge-scheduled',
      expired: 'status-badge-expired'
    }[announcement.status] || 'status-badge-inactive';

    return (
      <span className={`status-badge ${statusClass}`}>
        {announcement.status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-div">
        <div className="loading-text">Loading announcements...</div>
      </div>
    );
  }

  return (
    <div className="announcement-admin-container">
      <div style={{textAlign: 'left', marginBottom: '1rem'}}>
        <a href="/admin" className="back button-link">&larr; Back to Admin</a>
      </div>
      
      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="page-header" style={{marginBottom: '1.5rem', textAlign: 'center'}}>
        <h1 className="dashboard-header" style={{fontSize: '2.25rem'}}>Login Announcement Management</h1>
        <div style={{color: '#767676', marginTop: '0.25rem', fontSize: '1.15rem'}}>
          Control what users see when they log in. Messages are shown once per user until you create a new version.
        </div>
      </div>

      {/* Current Status Card */}
      {announcements.length > 0 && announcements[0].status === 'active' && (
        <div className="status-card">
          <div className="status-header">
            <h2 className="status-title">Current Announcement Status</h2>
            {getStatusBadge(announcements[0])}
          </div>
          <div className="status-info">
            <div className="status-item">
              <div className="status-label">Started</div>
              <div className="status-value">
                {new Date(announcements[0].created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="status-item">
              <div className="status-label">Users Seen</div>
              <div className="status-value">
                {announcements[0].acknowledgment_count} of {totalUsers} ({announcements[0].acknowledgment_percentage}%)
              </div>
            </div>
            <div className="status-item">
              <div className="status-label">Version</div>
              <div className="status-value">{announcements[0].version}</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {announcements.some(a => a.status === 'active') && (
        <div className="quick-actions">
          <h3 className="quick-actions-header">⚡ Quick Actions</h3>
          <div className="quick-action-buttons">
            <input 
              type="button"
              className="submit"
              value="Stop Current Announcement"
              onClick={() => handleDeactivate(announcements.find(a => a.status === 'active').id)}
            />
            <input 
              type="button"
              className="submit margin-left-sm"
              value="Reset All Views"
              onClick={() => handleResetAcknowledgments(announcements.find(a => a.status === 'active').id)}
            />
          </div>
        </div>
      )}

      {/* Edit Form */}
      <div className="form-card">
        <h2 className="form-header">
          {editingId ? 'Edit Announcement' : 'Create New Announcement'}
        </h2>
        
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="form-label">Announcement Title</label>
            <input
              type="text"
              className="form-input"
              name="title"
              value={currentAnnouncement.title}
              onChange={handleInputChange}
              maxLength="100"
              required
            />
            <div className="char-counter">
              {currentAnnouncement.title.length}/100 characters
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Message Content</label>
            <textarea
              className="form-input textarea"
              name="content"
              value={currentAnnouncement.content}
              onChange={handleInputChange}
              maxLength="1000"
              rows="6"
              required
            />
            <div className="form-help">Use plain text. HTML is not supported.</div>
            <div className="char-counter">
              {currentAnnouncement.content.length}/1000 characters
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="active"
                checked={currentAnnouncement.active}
                onChange={handleInputChange}
              />
              <span className="margin-left-sm">Show announcement to users on login</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Schedule Settings</label>
            <div className="schedule-controls">
              <div>
                <label className="form-label">Start Date & Time (Optional)</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  name="start_date"
                  value={currentAnnouncement.start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="form-label">End Date & Time (Optional)</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  name="end_date"
                  value={currentAnnouncement.end_date}
                  onChange={handleInputChange}
                />
                <div className="form-help">Leave empty for no automatic end date</div>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <input 
              type="submit" 
              className="submit float-right"
              disabled={saving}
              value={saving ? 'Saving...' : 'Save Changes & Activate'}
            />
            <input 
              type="button" 
              className="submit float-right margin-right-sm"
              value="Clear Form"
              onClick={() => {
                setCurrentAnnouncement({
                  title: '',
                  content: '',
                  active: false,
                  start_date: '',
                  end_date: ''
                });
                setEditingId(null);
              }}
            />
          </div>
        </form>
      </div>

      {/* Live Preview */}
      <div className="preview-section">
        <h3 className="preview-header">📱 Live Preview</h3>
        <div className="preview-content">
          <div className="preview-title">
            {currentAnnouncement.title || 'Announcement Title'}
          </div>
          <div className="preview-text">
            {currentAnnouncement.content ? 
              currentAnnouncement.content.split('\n').map((para, idx) => (
                para.trim() && <p key={idx}>{para}</p>
              )) : 
              <p>Your announcement content will appear here...</p>
            }
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="history-card">
        <h2 className="history-header">Announcement History</h2>
        {announcements.length > 0 ? (
          <table className="history-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Title</th>
                <th>Created</th>
                <th>Users Reached</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map(announcement => (
                <tr key={announcement.id}>
                  <td>{announcement.version}</td>
                  <td>{announcement.title}</td>
                  <td>{new Date(announcement.created_at).toLocaleDateString()}</td>
                  <td>
                    {announcement.acknowledgment_count}/{totalUsers}
                    {announcement.acknowledgment_percentage > 0 && 
                      ` (${announcement.acknowledgment_percentage}%)`
                    }
                  </td>
                  <td>{getStatusBadge(announcement)}</td>
                  <td>
                    <div className="action-buttons-small">
                      {announcement.status === 'inactive' && (
                        <>
                          <button 
                            className="btn-small"
                            onClick={() => handleActivate(announcement.id)}
                          >
                            Activate
                          </button>
                          <button 
                            className="btn-small"
                            onClick={() => loadAnnouncement(announcement)}
                          >
                            Reuse
                          </button>
                        </>
                      )}
                      {announcement.status === 'active' && (
                        <button 
                          className="btn-small danger"
                          onClick={() => handleDeactivate(announcement.id)}
                        >
                          Deactivate
                        </button>
                      )}
                      <button 
                        className="btn-small danger"
                        onClick={() => handleDelete(announcement.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="grey-text">No announcements created yet.</p>
        )}
      </div>
    </div>
  );
};

export default AnnouncementAdmin;
